import { formatDuration, formatPrescriptionMethod } from "@/lib/coachingFormatters";

const STEP_LABELS = {
  warm_up: "Warm-up",
  warmup: "Warm-up",
  work: "Work",
  active: "Main set",
  recovery: "Recovery",
  rest: "Rest",
  cool_down: "Cool-down",
  cooldown: "Cool-down",
  skill: "Skill",
};

const formatIntensity = (intensity, sport, prescriptionMethod) => {
  if (!intensity) return null;
  const method = (intensity.method || prescriptionMethod || "").toUpperCase();
  const unit = (intensity.unit || "").toUpperCase();
  const lower = intensity.lower;
  const upper = intensity.upper;
  const description = intensity.description;

  if (description) return description;

  const isPower = method === "POWER" || unit.includes("FTP") || unit.includes("WATT");
  const isHr = method === "HEART_RATE" || method === "HR" || unit.includes("HR");
  const isPace = method === "PACE" || unit.includes("PACE");
  const isRpe = method === "RPE" || unit.includes("RPE");
  const isCss = method === "CSS" || unit.includes("CSS");

  if (sport === "cycling" && !isPower && !isHr && !isRpe && unit.includes("ZONE")) {
    // zone labels are fine for cycling
  } else if (sport && sport !== "cycling" && isPower) {
    return null;
  }

  if (lower != null && upper != null) {
    if (isPower) return `${lower}–${upper}% FTP`;
    if (isHr) return `${lower}–${upper}% max HR`;
    if (isPace) return `${lower}–${upper} pace`;
    if (isRpe) return `RPE ${lower}–${upper}`;
    if (isCss) return `${lower}–${upper} CSS`;
    if (unit) return `${lower}–${upper} ${unit.replaceAll("_", " ").toLowerCase()}`;
    return `${lower}–${upper}`;
  }
  if (lower != null) {
    if (isRpe) return `RPE ${lower}`;
    return String(lower);
  }
  return null;
};

const formatStepDuration = (step) => {
  if (step?.duration_seconds != null) {
    return formatDuration(step.duration_seconds / 60);
  }
  if (step?.distance_meters != null) {
    const km = step.distance_meters / 1000;
    return km >= 1 ? `${km.toFixed(km >= 10 ? 0 : 1)} km` : `${Math.round(step.distance_meters)} m`;
  }
  return null;
};

export const formatWorkoutStep = (step, { sport, prescriptionMethod } = {}) => {
  if (!step) return null;
  const label = step.label
    || STEP_LABELS[String(step.step_type || "").toLowerCase()]
    || String(step.step_type || "Step").replaceAll("_", " ");
  const duration = formatStepDuration(step);
  const intensity = formatIntensity(step.intensity, sport, prescriptionMethod);
  const cadence = formatIntensity(step.cadence_target, sport, "CADENCE");
  const stroke = step.stroke_target?.description || null;
  const cue = step.athlete_cue || null;
  const repeat = step.repeat_count > 1 ? `${step.repeat_count}×` : null;

  return {
    label,
    duration,
    intensity,
    cadence,
    stroke,
    cue,
    repeat,
    substeps: (step.substeps || []).map((sub) =>
      formatWorkoutStep(sub, { sport, prescriptionMethod }),
    ).filter(Boolean),
  };
};

export const buildWorkoutPrescriptionSections = (template) => {
  if (!template) return [];
  const sport = template.sport;
  const prescriptionMethod = template.prescription?.preferred_method;
  const sections = [
    { key: "warmup", title: "Warm-up", steps: template.warmup || [] },
    { key: "main", title: "Main set", steps: template.main_set || [] },
    { key: "cooldown", title: "Cool-down", steps: template.cooldown || [] },
  ];

  return formatPrescriptionSections(sections, { sport, prescriptionMethod });
};

const formatPrescriptionSections = (sections, { sport, prescriptionMethod } = {}) =>
  sections
    .map((section) => ({
      ...section,
      items: section.steps
        .flatMap((step) => {
          const formatted = formatWorkoutStep(step, { sport, prescriptionMethod });
          if (!formatted) return [];
          if (formatted.repeat && formatted.substeps?.length) {
            return [{
              ...formatted,
              headline: `${formatted.repeat} ${formatted.label}`,
            }];
          }
          return [formatted];
        })
        .filter((item) => item.duration || item.intensity || item.cue),
    }))
    .filter((section) => section.items.length > 0);

export const buildSessionPrescriptionSections = (session) => {
  if (!session) return [];
  const sections = [
    { key: "warmup", title: "Warm-up", steps: session.warmup || [] },
    { key: "main", title: "Main set", steps: session.main_set || [] },
    { key: "cooldown", title: "Cool-down", steps: session.cooldown || [] },
  ];
  return formatPrescriptionSections(sections, {
    sport: session.sport,
    prescriptionMethod: session.prescription_method,
  });
};

export const sessionHasStructuredPrescription = (session) => Boolean(
  (session?.warmup || []).length
  || (session?.main_set || []).length
  || (session?.cooldown || []).length,
);

const cloneEditStep = (step) => ({
  step_type: step?.step_type || "ACTIVE",
  label: step?.label || "",
  duration_minutes: step?.duration_seconds != null
    ? String(Math.round((step.duration_seconds / 60) * 10) / 10)
    : "",
  intensity_text: step?.intensity?.description || "",
  athlete_cue: step?.athlete_cue || "",
  repeat_count: step?.repeat_count > 1 ? String(step.repeat_count) : "1",
  substeps: (step?.substeps || []).map(cloneEditStep),
});

export const clonePrescriptionForEdit = (session) => ({
  warmup: (session?.warmup || []).map(cloneEditStep),
  mainSet: (session?.main_set || []).map(cloneEditStep),
  cooldown: (session?.cooldown || []).map(cloneEditStep),
});

const editStepToPayload = (step) => {
  const durationMinutes = step.duration_minutes === "" || step.duration_minutes == null
    ? null
    : Number(step.duration_minutes);
  const repeatCount = step.repeat_count === "" || step.repeat_count == null
    ? 1
    : Number(step.repeat_count);
  const payload = {
    step_type: step.step_type || "ACTIVE",
    label: step.label?.trim() || null,
    duration_seconds: Number.isFinite(durationMinutes) && durationMinutes > 0
      ? Math.round(durationMinutes * 60)
      : null,
    repeat_count: Number.isFinite(repeatCount) && repeatCount >= 1
      ? Math.round(repeatCount)
      : 1,
    substeps: (step.substeps || []).map(editStepToPayload),
  };
  const intensityText = step.intensity_text?.trim();
  if (intensityText) {
    payload.intensity = { description: intensityText };
  }
  const cue = step.athlete_cue?.trim();
  if (cue) payload.athlete_cue = cue;
  return payload;
};

export const editPrescriptionToPayload = ({ warmup, mainSet, cooldown }) => ({
  warmup: (warmup || []).map(editStepToPayload),
  main_set: (mainSet || []).map(editStepToPayload),
  cooldown: (cooldown || []).map(editStepToPayload),
});

export const createBlankEditStep = () => ({
  step_type: "ACTIVE",
  label: "",
  duration_minutes: "",
  intensity_text: "",
  athlete_cue: "",
  repeat_count: "1",
  substeps: [],
});

export const formatTotalWorkoutDuration = (template, fallbackMinutes) => {
  const range = template?.duration;
  if (range?.minutes_min != null && range?.minutes_max != null) {
    if (range.minutes_min === range.minutes_max) {
      return formatDuration(range.minutes_min);
    }
    return `${formatDuration(range.minutes_min)} – ${formatDuration(range.minutes_max)}`;
  }
  if (range?.minutes_min != null) return formatDuration(range.minutes_min);
  if (range?.minutes_max != null) return formatDuration(range.minutes_max);
  if (fallbackMinutes != null) return formatDuration(fallbackMinutes);
  return null;
};

export const formatTrainingDaysSummary = (availabilitySummary) => {
  if (!availabilitySummary || typeof availabilitySummary !== "object") return null;
  const days = availabilitySummary.training_days
    || availabilitySummary.available_days
    || availabilitySummary.days;
  if (Array.isArray(days) && days.length) {
    return days.map((day) => String(day)).join(", ");
  }
  const dayWindows = availabilitySummary.day_windows || availabilitySummary.windows;
  if (dayWindows && typeof dayWindows === "object") {
    const names = Object.keys(dayWindows).filter((key) => dayWindows[key]);
    if (names.length) return names.join(", ");
  }
  if (typeof availabilitySummary.summary === "string") {
    return availabilitySummary.summary;
  }
  return null;
};

export { formatPrescriptionMethod };
